import { cloneElement, useMemo, useState } from "react";
import { ModalProps } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import Button from "../../Atoms/Button";
import BasicModal from "../../Atoms/BasicModal";
import LoadingButton from "../../Atoms/LoadingButton";
import { SearchCardProps } from "./components/SearchCard";

export type MultiCardModalProps = ModalProps & {
  cards: JSX.Element[];
  onSubmit: () => Promise<void>;
  submitLabel?: string;
  errorMessage?: string;
  title?: string;
  defaultExpandedCardIndex?: number;
  successMessage?: string;
};

export default function MultiCardModal({
  open,
  onClose,
  onSubmit,
  submitLabel,
  cards,
  errorMessage,
  successMessage,
  title,
  defaultExpandedCardIndex = 0,
}: MultiCardModalProps) {
  const [expandedCardIndex, setExpandedCardIndex] = useState<number | null>(
    defaultExpandedCardIndex
  );
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const expansionProps = (index: number) =>
    ({
      state: expandedCardIndex === index ? "EXPANDED" : "COLLAPSED",
      expandCard: () => setExpandedCardIndex(index),
      expandNextCard: () => setExpandedCardIndex(index + 1),
    } satisfies Pick<
      SearchCardProps,
      "state" | "expandCard" | "expandNextCard"
    >);

  const expandedCards = cards.map((component, index) =>
    cloneElement(component, expansionProps(index))
  );

  const buttonText = useMemo(
    () =>
      expandedCardIndex !== cards.length - 1
        ? "next"
        : errorMessage
        ? errorMessage
        : success
        ? successMessage
        : submitLabel ?? "submit",
    [
      expandedCardIndex,
      cards.length,
      errorMessage,
      success,
      successMessage,
      submitLabel,
    ]
  );

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title={title}
      endComponent={
        expandedCardIndex !== cards.length - 1 ? (
          <Button
            dark
            onClick={() => {
              setExpandedCardIndex((index) => (index === null ? 0 : index + 1));
            }}
          >
            {buttonText}
          </Button>
        ) : (
          <div style={{ position: "relative" }}>
            <LoadingButton
              success={success}
              loading={loading}
              disabled={loading || !!errorMessage || success}
              onClick={async () => {
                setLoading(true);
                await onSubmit();
                setSuccess(!!successMessage);
                setLoading(false);

                setTimeout(() => {
                  onClose();
                  setTimeout(() => {
                    setExpandedCardIndex(defaultExpandedCardIndex);
                    setSuccess(false);
                    // set a 300ms delay on expanding the first card
                    // so it happens after the modal has faded away
                  }, 300);
                  // set a 2000ms delay on closing the card
                  // so the user can read the success message
                }, Number(!!successMessage) * 2000);
              }}
            >
              {buttonText}
            </LoadingButton>
          </div>
        )
      }
    >
      {expandedCards}
    </BasicModal>
  );
}
